/** Server list table using TanStack Table with sorting. */
import React, { useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  Chip,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  createColumnHelper,
  SortingState,
  flexRender,
} from "@tanstack/react-table";
import { Server } from "../../types";
import { fleetApi } from "../../api/client";
import { publicRoleName } from "../../branding";
import StatusIndicator from "./StatusIndicator";

interface ServerListProps {
  servers: Server[];
  onView?: (server: Server) => void;
  onRemove?: (serverId: string) => void;
  onDownloadScript?: (serverId: string) => void;
}

/** Column helper for TanStack Table. */
const columnHelper = createColumnHelper<Server>();

/** Server list table with sortable columns and row actions. */
export default function ServerList({
  servers,
  onView,
  onRemove,
  onDownloadScript,
}: ServerListProps) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [reconciling, setReconciling] = React.useState(false);
  const [reconcileResult, setReconcileResult] = React.useState<{
    ok?: boolean;
    summary?: string;
    error?: string;
  } | null>(null);

  /** One-click convergence: dispatcher + FS gateways + IP auth (Phase 2.4). */
  const handleReconcile = async () => {
    setReconciling(true);
    setReconcileResult(null);
    try {
      const resp = await fleetApi.reconcile();
      const report = resp.data as {
        ok?: boolean;
        dispatcher?: Record<string, unknown>;
        fs_gateways?: Record<string, unknown>;
        ip_auth?: Record<string, unknown>;
      };
      const summary = [
        report.dispatcher
          ? `dispatcher +${report.dispatcher["added"] ?? 0} ~${report.dispatcher["updated"] ?? 0} -${report.dispatcher["removed"] ?? 0}`
          : null,
        report.fs_gateways ? `gateways ${report.fs_gateways["created"] ?? 0}` : null,
        report.ip_auth ? `addresses ${report.ip_auth["addresses"] ?? 0}` : null,
      ]
        .filter(Boolean)
        .join(" · ");
      setReconcileResult({ ok: report.ok !== false, summary });
    } catch (err: unknown) {
      const detail =
        err && typeof err === "object" && "response" in err
          ? ((err as { response?: { data?: { detail?: string } } }).response?.data
              ?.detail ?? "reconcile failed")
          : "reconcile failed";
      setReconcileResult({ ok: false, error: String(detail) });
    } finally {
      setReconciling(false);
    }
  };

  /** Format percentage value. */
  const formatPercent = (value: number | null): string => {
    return value !== null ? `${value}%` : "-";
  };

  /** Table columns definition. */
  const columns = useMemo(
    () => [
      columnHelper.accessor("hostname", {
        header: "Hostname",
        cell: (info) => info.getValue(),
        enableSorting: true,
      }),
      columnHelper.accessor("server_type", {
        header: "Type",
        cell: (info) => publicRoleName(info.getValue()),
        enableSorting: true,
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => <StatusIndicator status={info.getValue()} showText={true} />,
        enableSorting: false,
      }),
      columnHelper.accessor("cpu_percent", {
        header: "CPU",
        cell: (info) => formatPercent(info.getValue()),
        enableSorting: true,
      }),
      columnHelper.accessor("memory_percent", {
        header: "Memory",
        cell: (info) => formatPercent(info.getValue()),
        enableSorting: true,
      }),
      columnHelper.accessor("active_calls", {
        header: "Active Calls",
        cell: (info) => info.getValue().toLocaleString(),
        enableSorting: true,
      }),
      columnHelper.accessor("last_drift", {
        header: "Drift",
        cell: (info) => {
          const drift = info.getValue();
          if (!drift) {
            return <Typography variant="body2" color="text.secondary">-</Typography>;
          }
          if (drift.error) {
            return (
              <Tooltip title={`Convergence error: ${drift.error}`}>
                <Chip size="small" color="warning" label="error" />
              </Tooltip>
            );
          }
          if (drift.drifted) {
            const detail = [
              `artifact: ${drift.artifact}`,
              drift.applied ? "apply attempted" : "not applied",
              drift.verified === true
                ? "verified in sync"
                : drift.verified === false
                  ? "verify failed"
                  : "unverified",
              drift.checked_at ? `checked ${drift.checked_at}` : "",
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <Tooltip title={detail}>
                <Chip
                  size="small"
                  color={drift.verified === true ? "success" : "error"}
                  label={drift.verified === true ? "repaired" : "drifted"}
                />
              </Tooltip>
            );
          }
          return (
            <Tooltip title="Agent verified desired state">
              <Chip size="small" color="success" variant="outlined" label="in sync" />
            </Tooltip>
          );
        },
        enableSorting: false,
      }),
      columnHelper.accessor("created_at", {
        header: "Created",
        cell: (info) => {
          const date = new Date(info.getValue());
          return date.toLocaleDateString();
        },
        enableSorting: true,
      }),
      columnHelper.accessor((row) => row.id, {
        id: "actions",
        header: "Actions",
        cell: (info) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {onView && (
              <Tooltip title="View Details">
                <IconButton
                  size="small"
                  onClick={() => onView(servers[info.row.index])}
                  color="primary"
                 aria-label="View">
                  <ViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDownloadScript && (
              <Tooltip title="Download Join Script">
                <IconButton
                  size="small"
                  onClick={() => onDownloadScript(info.row.id)}
                  color="info"
                 aria-label="Download">
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onRemove && (
              <Tooltip title="Remove Server">
                <IconButton
                  size="small"
                  onClick={() => onRemove(info.row.id)}
                  color="error"
                 aria-label="Delete">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
        enableSorting: false,
      }),
    ],
    [servers, onView, onDownloadScript, onRemove]
  );

  /** Table instance with sorting. */
  const table = useReactTable({
    data: servers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  });

  if (servers.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          No servers found. Click "Add Server" to create your first server.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={
            reconciling ? <CircularProgress size={16} /> : <SyncIcon />
          }
          onClick={handleReconcile}
          disabled={reconciling}
        >
          Reconcile fleet
        </Button>
        <Typography variant="caption" color="text.secondary">
          Converges dispatcher, sofia gateways and carrier IP auth to desired
          state and reloads the affected services.
        </Typography>
      </Box>
      {reconcileResult && (
        <Alert
          severity={reconcileResult.ok ? "success" : "error"}
          onClose={() => setReconcileResult(null)}
          sx={{ mb: 1 }}
        >
          {reconcileResult.ok
            ? `Convergence complete${reconcileResult.summary ? `: ${reconcileResult.summary}` : ""}`
            : `Convergence failed: ${reconcileResult.error ?? "unknown error"}`}
        </Alert>
      )}
      <TableContainer component={Paper} elevation={2}>
      <Table>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableCell
                  key={header.id}
                  sortDirection={header.column.getIsSorted() ? (header.column.getIsSorted() as "asc" | "desc") : false}
                  sx={{ fontWeight: "bold" }}
                >
                  {header.column.getCanSort() ? (
                    <TableSortLabel
                      active={header.column.getIsSorted() !== false}
                      direction={header.column.getIsSorted() === "asc" ? "asc" : "desc"}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableSortLabel>
                  ) : (
                    flexRender(header.column.columnDef.header, header.getContext())
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              hover
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </TableContainer>
    </Box>
  );
}
